import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Download, Upload, Trash2, Save, X, Edit, Search, ArrowUpDown, Database, Image as ImageIcon, Calendar, Clock, TrendingUp, Eye, MousePointer, DollarSign, Target, Plus, Link as LinkIcon, FolderOpen, ChevronDown, Check, Users, Tag, History, Undo2 } from 'lucide-react';
import CreativeImage from '../../components/CreativeImage';
import { listCreatives, uploadCreative } from '../../utils/adminApi';

const TEAL = '#32B4AC';

// ---------- BrandMultiSelect — searchable multi-select with chips + Select-All ----------
const BrandMultiSelect = ({ brands, selectedIds, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return brands.filter((b) => !q || b.name.toLowerCase().includes(q));
  }, [brands, search]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((b) => selectedIds.includes(b.id));
  const toggle = (id) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };
  const selectAll = () => onChange(Array.from(new Set([...selectedIds, ...filtered.map((b) => b.id)])));
  const clearAll = () => onChange([]);

  const label =
    selectedIds.length === 0 ? 'All brands'
    : selectedIds.length === 1 ? (brands.find((b) => b.id === selectedIds[0])?.name || '1 brand')
    : `${selectedIds.length} brands`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid="brand-multiselect-trigger"
        className="w-full h-8 px-2 text-xs bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-md text-white dark:text-white light:text-slate-900 hover:bg-slate-800 transition-colors flex items-center justify-between gap-1"
      >
        <span className="truncate">{label}</span>
        <ChevronDown size={12} className="flex-shrink-0 opacity-70" />
      </button>
      {open && (
        <>
          {isMobile && <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setOpen(false)} />}
          <div
            data-testid="brand-multiselect-panel"
            className={
              isMobile
                ? 'fixed inset-x-0 bottom-0 z-50 max-h-[70vh] bg-slate-900 light:bg-white rounded-t-2xl shadow-2xl border-t border-slate-700 flex flex-col'
                : 'absolute z-50 top-[calc(100%+4px)] left-0 w-[260px] bg-slate-900 light:bg-white border border-slate-700 light:border-slate-300 rounded-md shadow-xl'
            }
          >
            <div className="p-2 border-b border-slate-700 light:border-slate-200">
              <div className="relative">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search brands..." data-testid="brand-multiselect-search"
                  className="w-full pl-7 pr-2 py-1.5 text-[11px] bg-slate-800 light:bg-slate-50 border border-slate-700 light:border-slate-300 rounded text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <button onClick={selectAll} data-testid="brand-multiselect-select-all"
                  className="text-[10px] font-semibold text-teal-400 hover:text-teal-300">
                  Select all{filtered.length !== brands.length ? ' (filtered)' : ''}
                </button>
                <button onClick={clearAll} data-testid="brand-multiselect-clear"
                  className="text-[10px] font-semibold text-slate-400 hover:text-slate-200">
                  Clear all
                </button>
              </div>
            </div>
            <div className={`overflow-y-auto py-1 ${isMobile ? 'flex-1' : 'max-h-[240px]'}`}>
              {filtered.length === 0 ? (
                <p className="text-[10px] text-slate-400 text-center py-3">No matches</p>
              ) : filtered.map((b) => {
                const checked = selectedIds.includes(b.id);
                return (
                  <button key={b.id} onClick={() => toggle(b.id)}
                    data-testid={`brand-multiselect-option-${b.name}`}
                    className={`w-full text-left flex items-center gap-2 px-3 py-1.5 text-[11px] ${checked ? 'bg-teal-500/10 text-white light:text-slate-900' : 'text-slate-200 light:text-slate-700 hover:bg-slate-800 light:hover:bg-slate-100'}`}>
                    <span className={`flex items-center justify-center w-3.5 h-3.5 rounded border ${checked ? 'border-teal-400' : 'border-slate-600 light:border-slate-400'}`}
                      style={checked ? { backgroundColor: TEAL } : {}}>
                      {checked && <Check size={9} className="text-white" />}
                    </span>
                    <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: b.color || '#64748b' }} />
                    <span className="flex-1 truncate">{b.name}</span>
                  </button>
                );
              })}
            </div>
            {isMobile && (
              <button onClick={() => setOpen(false)} className="px-4 py-3 text-xs font-semibold text-white border-t border-slate-700"
                style={{ backgroundColor: TEAL }}>
                Done ({selectedIds.length} selected)
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const EMPTY_RECORD = {
  brand_id: '',
  name: '',
  date: '',
  sent: 0,
  delivered: 0,
  seen: 0,
  clicks: 0,
  ctr: 0,
  net_spends: 0,
  delivery_rate: 0,
  sales: 0,
  roas: 0,
  attribution_setting: '',
  cohort: '',
  landing_page: '',
  creative: '',
};

const AllCampaignsData = ({ showToast, selectedBrand, isViewOnly, onCompare }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isViewer = user?.role === 'viewer';

  const [campaigns, setCampaigns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [uploadingCreative, setUploadingCreative] = useState(null);
  const [creativeUrlPrompt, setCreativeUrlPrompt] = useState(null); // campaign.id when prompting

  // Compare mode — flips card layout to selectable checkboxes and shows a
  // floating action bar. Available to all roles (viewers can compare read-only).
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const toggleCompareMode = () => {
    setCompareMode((prev) => {
      if (prev) setSelectedForCompare([]);
      return !prev;
    });
  };
  const toggleCompareSelect = (id) => {
    setSelectedForCompare((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 5) {
        showToast?.('Maximum 5 campaigns can be compared at once', 'error');
        return prev;
      }
      return [...prev, id];
    });
  };
  const launchCompare = () => {
    if (selectedForCompare.length < 2) {
      showToast?.('Select at least 2 campaigns to compare', 'error');
      return;
    }
    onCompare?.(selectedForCompare);
  };
  const [libraryPickerFor, setLibraryPickerFor] = useState(null);    // campaign.id when opening library picker

  // Add Record modal
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newRecord, setNewRecord] = useState({ ...EMPTY_RECORD });
  const [savingNew, setSavingNew] = useState(false);

  // Edit-history modal state
  const [historyCampaign, setHistoryCampaign] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRevertingId, setHistoryRevertingId] = useState(null);

  const reloadHistory = async (campaignId) => {
    setHistoryLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/campaigns-data/${campaignId}/audit`,
        { headers: { 'X-User-Id': user?.id || '' } }
      );
      const data = await res.json();
      setHistoryEntries(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast?.('Failed to load history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistory = async (campaign) => {
    setHistoryCampaign(campaign);
    await reloadHistory(campaign.id);
  };

  const handleHistoryRevert = async (entry) => {
    if (entry.reverted || entry.revert) {
      showToast?.(entry.revert ? 'Cannot revert a revert entry' : 'Already reverted', 'error');
      return;
    }
    const fieldList = (entry.changes || []).map((c) => c.field).join(', ');
    const ok = window.confirm(
      `Roll back this edit?\n\nFields: ${fieldList}\n\n` +
      `Values will be restored to what they were before this edit. ` +
      `The rollback will be recorded in the audit log.`
    );
    if (!ok) return;
    setHistoryRevertingId(entry.id);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/audit-log/${entry.id}/revert`,
        { method: 'POST', headers: { 'X-User-Id': user?.id || '' } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Revert failed');
      }
      showToast?.('Row reverted to previous values', 'success');
      await loadCampaigns();
      await reloadHistory(historyCampaign.id);
    } catch (e) {
      showToast?.(e.message || 'Failed to revert', 'error');
    } finally {
      setHistoryRevertingId(null);
    }
  };

  // Filter and sort states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBrand, setFilterBrand] = useState('all'); // legacy, retained for compat
  const [selectedBrandIds, setSelectedBrandIds] = useState([]); // multi-select — empty = all
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // desc = newest first

  useEffect(() => {
    loadCampaigns();
    loadBrands();
  }, [selectedBrand]);

  const loadCampaigns = async () => {
    try {
      // Determine brand_id filter:
      //  - admins: no filter (see all)
      //  - viewers: use selectedBrand from BrandSelector
      //  - brand managers: use their own brand_id
      let brandFilter = null;
      if (isViewer) brandFilter = selectedBrand;
      else if (!isAdmin) brandFilter = user.brand_id;
      
      const url = brandFilter
        ? `${process.env.REACT_APP_BACKEND_URL}/api/campaigns-data?brand_id=${brandFilter}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/campaigns-data`;
      const response = await fetch(url);
      const data = await response.json();
      setCampaigns(data);
    } catch (error) {
      showToast('Failed to load campaigns data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadBrands = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brands`);
      const data = await response.json();
      setBrands(data);
    } catch (error) {
      console.error('Failed to load brands:', error);
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : 'Unknown';
  };

  const getBrandColor = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand?.color || '#2EB4AC';
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/campaigns-data/template`);
      const data = await response.json();
      
      const blob = new Blob([data.template], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'campaigns_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('Template downloaded', 'success');
    } catch (error) {
      showToast('Failed to download template', 'error');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.csv$/i)) {
      showToast('Please upload a .csv file', 'error');
      e.target.value = '';
      return;
    }

    setUploadingFile(true);
    try {
      let text = await file.text();
      if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);

      const result = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/campaigns-data/upload-csv`,
        { csv_content: text }
      );

      const added = result.data?.campaigns_added || 0;
      const errs = result.data?.errors || [];
      const skipped = result.data?.skipped_empty || 0;
      const parts = [];
      if (added) parts.push(`Added ${added}`);
      if (skipped) parts.push(`Skipped ${skipped} empty`);
      if (errs.length) parts.push(`${errs.length} error${errs.length === 1 ? '' : 's'}`);
      if (parts.length === 0) parts.push('Nothing to import');
      showToast(parts.join(' · '), errs.length && !added ? 'error' : 'success');
      if (errs.length) console.warn('CSV import errors:', errs);
      loadCampaigns();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to upload file';
      showToast(msg, 'error');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleCreativeUpload = async (campaignId, file) => {
    if (!file) return;

    setUploadingCreative(campaignId);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/campaigns-data/${campaignId}/creative`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      showToast('Creative uploaded successfully', 'success');
      loadCampaigns();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to upload creative';
      showToast(msg, 'error');
    } finally {
      setUploadingCreative(null);
    }
  };

  const handleCreativeUrlSave = async (campaignId, url) => {
    const trimmed = (url || '').trim();
    if (!trimmed) {
      showToast('Please paste a URL', 'error');
      return;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
      showToast('URL must start with http:// or https://', 'error');
      return;
    }
    setUploadingCreative(campaignId);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/campaigns-data/${campaignId}/creative-url`,
        { url: trimmed }
      );
      showToast('Creative URL saved', 'success');
      setCreativeUrlPrompt(null);
      loadCampaigns();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save URL';
      showToast(msg, 'error');
    } finally {
      setUploadingCreative(null);
    }
  };

  // Link a Creative Library item (by relative /api/files/... URL) to the campaign row.
  // Reuses the existing /creative-url endpoint, which now accepts /api/files/... refs.
  const handleLinkFromLibrary = async (campaignId, libraryUrl) => {
    if (!libraryUrl) return;
    setUploadingCreative(campaignId);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/campaigns-data/${campaignId}/creative-url`,
        { url: libraryUrl }
      );
      showToast('Creative linked from library', 'success');
      setLibraryPickerFor(null);
      loadCampaigns();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to link creative';
      showToast(msg, 'error');
    } finally {
      setUploadingCreative(null);
    }
  };

  const openAddModal = () => {
    setNewRecord({
      ...EMPTY_RECORD,
      brand_id: brands[0]?.id || '',
      date: new Date().toISOString().slice(0, 10),
    });
    setAddModalOpen(true);
  };

  const handleAddRecord = async () => {
    if (!newRecord.name?.trim()) {
      showToast('Campaign name is required', 'error');
      return;
    }
    if (!newRecord.brand_id) {
      showToast('Brand is required', 'error');
      return;
    }
    if (!newRecord.date) {
      showToast('Date is required', 'error');
      return;
    }
    setSavingNew(true);
    try {
      const payload = {
        ...newRecord,
        sent: Number(newRecord.sent) || 0,
        delivered: Number(newRecord.delivered) || 0,
        seen: Number(newRecord.seen) || 0,
        clicks: Number(newRecord.clicks) || 0,
        ctr: Number(newRecord.ctr) || 0,
        net_spends: Number(newRecord.net_spends) || 0,
        delivery_rate: Number(newRecord.delivery_rate) || 0,
        sales: Number(newRecord.sales) || 0,
        roas: Number(newRecord.roas) || 0,
        image: newRecord.creative || '',
      };
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/campaigns-data`,
        payload
      );
      showToast('Record added', 'success');
      setAddModalOpen(false);
      setNewRecord({ ...EMPTY_RECORD });
      loadCampaigns();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to add record';
      showToast(msg, 'error');
    } finally {
      setSavingNew(false);
    }
  };

  const startEdit = (campaign) => {
    setEditingCampaign(campaign);
  };

  const cancelEdit = () => {
    setEditingCampaign(null);
  };

  const saveEdit = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/campaigns-data/${editingCampaign.id}`,
        editingCampaign
      );
      showToast('Campaign updated successfully', 'success');
      setEditingCampaign(null);
      loadCampaigns();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to update campaign';
      showToast(msg, 'error');
    }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;

    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/campaigns-data/${id}`);
      showToast('Campaign deleted successfully', 'success');
      loadCampaigns();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete campaign';
      showToast(msg, 'error');
    }
  };

  // Filter and sort campaigns
  const filteredCampaigns = campaigns
    .filter(c => {
      const matchesSearch = c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           getBrandName(c.brand_id).toLowerCase().includes(searchTerm.toLowerCase());
      const matchesBrand = selectedBrandIds.length === 0 || selectedBrandIds.includes(c.brand_id);
      const matchesDateFrom = !filterDateFrom || new Date(c.date) >= new Date(filterDateFrom);
      const matchesDateTo = !filterDateTo || new Date(c.date) <= new Date(filterDateTo);
      
      return matchesSearch && matchesBrand && matchesDateFrom && matchesDateTo;
    })
    .sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

  const formatNumber = (num) => {
    if (!num && num !== 0) return '—';
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const formatCurrency = (num) => {
    if (!num && num !== 0) return '—';
    return '₹' + new Intl.NumberFormat('en-IN').format(num);
  };

  return (
    <div className="p-5 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h1 className="text-lg font-bold text-white dark:text-white light:text-slate-900 flex items-center gap-2">
              <Database size={16} style={{ color: TEAL }} />
              Campaign Analytics
            </h1>
            <p className="text-[11px] text-slate-400 dark:text-slate-400 light:text-slate-600 mt-0.5">
              Historical campaign data with detailed metrics and performance insights
            </p>
          </div>

          <div className="flex gap-2 items-center flex-wrap">
            {/* Compare Mode toggle — available to all roles */}
            <button
              onClick={toggleCompareMode}
              data-testid="campaigns-compare-toggle"
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 border ${
                compareMode
                  ? 'bg-teal-600 text-white border-teal-600 hover:bg-teal-700'
                  : 'bg-slate-700 dark:bg-slate-700 light:bg-white text-white dark:text-white light:text-slate-700 border-slate-600 dark:border-slate-600 light:border-slate-300 hover:bg-slate-600'
              }`}
              title={compareMode ? 'Cancel comparison' : 'Compare campaigns side-by-side'}
            >
              <TrendingUp size={13} />
              {compareMode ? `Cancel (${selectedForCompare.length})` : 'Compare'}
            </button>

            {isAdmin && (
              <>
                <button
                  onClick={openAddModal}
                  data-testid="campaigns-data-add-record"
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg text-white transition-all flex items-center gap-1.5 hover:opacity-90"
                  style={{ backgroundColor: TEAL }}
                >
                  <Plus size={13} />
                  Add Record
                </button>

                <button
                  onClick={downloadTemplate}
                  className="px-3 py-1.5 text-xs font-medium bg-slate-700 dark:bg-slate-700 light:bg-white hover:bg-slate-600 text-white dark:text-white light:text-slate-700 rounded-lg transition-all flex items-center gap-1.5 border border-slate-600 dark:border-slate-600 light:border-slate-300"
                >
                  <Download size={13} />
                  Template
                </button>

                <label
                  data-testid="campaigns-data-upload-csv"
                  className="px-3 py-1.5 text-xs font-semibold hover:opacity-90 text-white rounded-lg cursor-pointer transition-all flex items-center gap-1.5"
                  style={{ backgroundColor: TEAL }}
                >
                  <Upload size={13} />
                  {uploadingFile ? 'Uploading...' : 'Upload CSV'}
                  <input type="file" accept=".csv" onChange={handleFileUpload} disabled={uploadingFile} className="hidden" />
                </label>
              </>
            )}
          </div>
        </div>

        {/* Filters — compact, responsive */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2 bg-slate-800/50 dark:bg-slate-800/50 light:bg-slate-100 p-2.5 rounded-lg border border-slate-700 dark:border-slate-700 light:border-slate-200">
          <div className="lg:col-span-2">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-400 light:text-slate-600 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-slate-400" size={13} />
              <input
                type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Campaign or brand..."
                className="w-full h-8 pl-8 pr-2 text-xs bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-md text-white dark:text-white light:text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-400 light:text-slate-600 mb-1">Brand</label>
            <BrandMultiSelect
              brands={isAdmin || isViewer ? brands : brands.filter((b) => b.id === user?.brand_id)}
              selectedIds={selectedBrandIds}
              onChange={setSelectedBrandIds}
            />
          </div>

          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-400 light:text-slate-600 mb-1">From</label>
            <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)}
              className="w-full h-8 px-2 text-xs bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-md text-white dark:text-white light:text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>

          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-400 light:text-slate-600 mb-1">To</label>
            <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)}
              className="w-full h-8 px-2 text-xs bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-md text-white dark:text-white light:text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>

          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-400 light:text-slate-600 mb-1">Sort</label>
            <button onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="w-full h-8 px-2 text-xs bg-slate-900 dark:bg-slate-900 light:bg-white border border-slate-700 dark:border-slate-700 light:border-slate-300 rounded-md text-white dark:text-white light:text-slate-900 hover:bg-slate-800 transition-colors flex items-center justify-center gap-1.5">
              <ArrowUpDown size={12} />
              {sortOrder === 'desc' ? 'Newest' : 'Oldest'}
            </button>
          </div>
        </div>

        {/* Selected brand chips */}
        {selectedBrandIds.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="brand-chips">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Selected:</span>
            {selectedBrandIds.map((id) => {
              const b = brands.find((x) => x.id === id);
              if (!b) return null;
              return (
                <span key={id} data-testid={`brand-chip-${b.name}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white"
                  style={{ backgroundColor: b.color || TEAL }}>
                  {b.name}
                  <button onClick={() => setSelectedBrandIds(selectedBrandIds.filter((x) => x !== id))}
                    className="hover:bg-white/20 rounded-full" data-testid={`brand-chip-remove-${b.name}`}>
                    <X size={10} />
                  </button>
                </span>
              );
            })}
            <button onClick={() => setSelectedBrandIds([])} data-testid="brand-chips-clear-all"
              className="text-[10px] font-semibold text-slate-400 hover:text-slate-200 underline ml-1">
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Campaign Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-slate-400 text-lg">Loading campaigns...</div>
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-slate-800/30 dark:bg-slate-800/30 light:bg-slate-100 rounded-xl border-2 border-dashed border-slate-700 dark:border-slate-700 light:border-slate-300">
          <Database className="text-slate-600 mb-4" size={48} />
          <p className="text-slate-400 dark:text-slate-400 light:text-slate-600 text-lg">
            No campaigns found
          </p>
          <p className="text-slate-500 dark:text-slate-500 light:text-slate-500 text-sm mt-2">
            Upload CSV to add historical campaign data
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {filteredCampaigns.map((campaign) => {
            const isEditing = editingCampaign?.id === campaign.id;
            const displayCampaign = isEditing ? editingCampaign : campaign;
            const brandColor = getBrandColor(campaign.brand_id);
            const isSelected = compareMode && selectedForCompare.includes(campaign.id);

            return (
              <div
                key={campaign.id}
                onClick={compareMode ? () => toggleCompareSelect(campaign.id) : undefined}
                className={`relative bg-slate-800 dark:bg-slate-800 light:bg-white rounded-xl border-2 overflow-hidden transition-all ${
                  compareMode ? 'cursor-pointer' : ''
                } ${
                  isSelected
                    ? 'border-teal-500 ring-2 ring-teal-500/40 shadow-teal-500/20 shadow-lg'
                    : compareMode
                      ? 'border-slate-700 dark:border-slate-700 light:border-slate-200 hover:border-teal-400/60'
                      : 'border-slate-700 dark:border-slate-700 light:border-slate-200 hover:shadow-2xl'
                }`}
                data-testid={`campaign-card-${campaign.id}`}
              >
                {/* Compare-mode selection checkbox */}
                {compareMode && (
                  <div
                    className="absolute top-3 right-3 z-20"
                    data-testid={`campaign-compare-checkbox-${campaign.id}`}
                  >
                    <div
                      className={`w-7 h-7 rounded-md flex items-center justify-center border-2 transition-all ${
                        isSelected
                          ? 'bg-teal-500 border-teal-400 text-white'
                          : 'bg-slate-900/80 border-slate-500 text-transparent'
                      }`}
                    >
                      <Check size={16} />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-12 gap-6 p-6">
                  {/* Creative Section */}
                  <div className="col-span-12 md:col-span-3">
                    <div className="space-y-3">
                      <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide">
                        Creative
                      </label>
                      {displayCampaign.creative ? (
                        <div className="relative group">
                          <CreativeImage
                            src={displayCampaign.creative}
                            alt={`${displayCampaign.name} creative`}
                            minHeight="min-h-[200px]"
                          />
                          {isAdmin && (
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-xl">
                              <label className="px-3 py-1.5 bg-white/95 hover:bg-white text-slate-900 rounded-lg cursor-pointer text-xs font-medium flex items-center gap-1.5">
                                <Upload size={12} />
                                Replace
                                <input
                                  type="file"
                                  accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                                  onChange={(e) => handleCreativeUpload(campaign.id, e.target.files[0])}
                                  className="hidden"
                                />
                              </label>
                              <button
                                onClick={() => setLibraryPickerFor(campaign.id)}
                                className="px-3 py-1.5 bg-white/95 hover:bg-white text-slate-900 rounded-lg text-xs font-medium flex items-center gap-1.5"
                                data-testid={`campaigns-data-link-library-${campaign.id}`}
                              >
                                <FolderOpen size={12} />
                                Library
                              </button>
                              <button
                                onClick={() => setCreativeUrlPrompt(campaign.id)}
                                className="px-3 py-1.5 bg-white/95 hover:bg-white text-slate-900 rounded-lg text-xs font-medium flex items-center gap-1.5"
                                data-testid={`campaigns-data-paste-url-${campaign.id}`}
                              >
                                <LinkIcon size={12} />
                                Paste URL
                              </button>
                            </div>
                          )}
                        </div>
                      ) : isAdmin ? (
                        <div className="space-y-2">
                          <label className="w-full min-h-[200px] border-2 border-dashed border-slate-600 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-teal-500 transition-colors bg-slate-900/50 py-8">
                            <ImageIcon className="text-slate-500 mb-2" size={32} />
                            <span className="text-slate-400 text-sm">Upload Creative</span>
                            <span className="text-slate-500 text-[10px] mt-1">JPG, PNG, WEBP · any ratio</span>
                            <input
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                              onChange={(e) => handleCreativeUpload(campaign.id, e.target.files[0])}
                              className="hidden"
                            />
                          </label>
                          <button
                            onClick={() => setLibraryPickerFor(campaign.id)}
                            data-testid={`campaigns-data-link-library-empty-${campaign.id}`}
                            className="w-full px-3 py-2 border border-slate-600 hover:border-teal-500 text-slate-300 rounded-xl text-xs flex items-center justify-center gap-2 transition-colors"
                          >
                            <FolderOpen size={12} />
                            Link from Creative Library
                          </button>
                          <button
                            onClick={() => setCreativeUrlPrompt(campaign.id)}
                            data-testid={`campaigns-data-add-url-${campaign.id}`}
                            className="w-full px-3 py-2 border border-slate-600 hover:border-teal-500 text-slate-300 rounded-xl text-xs flex items-center justify-center gap-2 transition-colors"
                          >
                            <LinkIcon size={12} />
                            Or paste image / Drive URL
                          </button>
                        </div>
                      ) : (
                        <CreativeImage src={null} minHeight="min-h-[200px]" />
                      )}

                      {/* Inline URL prompt */}
                      {creativeUrlPrompt === campaign.id && (
                        <div className="bg-slate-900/80 border border-slate-700 rounded-lg p-2 space-y-2">
                          <input
                            type="url"
                            placeholder="https://drive.google.com/..."
                            data-testid={`campaigns-data-url-input-${campaign.id}`}
                            className="w-full px-3 py-2 text-xs bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleCreativeUrlSave(campaign.id, e.target.value);
                              if (e.key === 'Escape') setCreativeUrlPrompt(null);
                            }}
                            autoFocus
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => {
                                const input = e.currentTarget.closest('div').parentElement.querySelector('input[type="url"]');
                                handleCreativeUrlSave(campaign.id, input.value);
                              }}
                              className="flex-1 px-2 py-1.5 text-xs text-white rounded-lg hover:opacity-90"
                              style={{ backgroundColor: TEAL }}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setCreativeUrlPrompt(null)}
                              className="px-2 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {uploadingCreative === campaign.id && (
                        <p className="text-xs text-teal-400 text-center">Uploading...</p>
                      )}
                    </div>
                  </div>

                  {/* Campaign Details & Metrics */}
                  <div className="col-span-12 md:col-span-9">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        {isEditing ? (
                          <input
                            type="text"
                            value={displayCampaign.name}
                            onChange={(e) => setEditingCampaign({...editingCampaign, name: e.target.value})}
                            className="text-2xl font-bold bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white w-full"
                          />
                        ) : (
                          <h3 className="text-base font-bold text-white dark:text-white light:text-slate-900">
                            {displayCampaign.name}
                          </h3>
                        )}
                        
                        <div className="flex items-center gap-4 mt-2 flex-wrap">
                          <span
                            className="px-3 py-1 rounded-full text-sm font-medium text-white"
                            style={{ backgroundColor: brandColor }}
                          >
                            {getBrandName(campaign.brand_id)}
                          </span>
                          
                          {isEditing ? (
                            <>
                              <input
                                type="date"
                                value={displayCampaign.date}
                                onChange={(e) => setEditingCampaign({...editingCampaign, date: e.target.value})}
                                className="px-2 py-1 bg-slate-900 border border-slate-600 rounded text-white text-sm"
                              />
                              <input
                                type="text"
                                value={displayCampaign.slot || ''}
                                onChange={(e) => setEditingCampaign({...editingCampaign, slot: e.target.value})}
                                placeholder="Slot"
                                className="px-2 py-1 bg-slate-900 border border-slate-600 rounded text-white text-sm w-24"
                              />
                            </>
                          ) : (
                            <>
                              <span className="text-slate-400 flex items-center gap-1">
                                <Calendar size={14} />
                                {new Date(campaign.date).toLocaleDateString('en-IN')}
                              </span>
                              {campaign.slot && (
                                <span className="text-slate-400 flex items-center gap-1">
                                  <Clock size={14} />
                                  {campaign.slot}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      {isAdmin && (
                        <div className="flex items-center gap-2">
                          {isEditing ? (
                            <>
                              <button
                                onClick={saveEdit}
                                className="p-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
                                title="Save"
                              >
                                <Save size={18} />
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="p-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                                title="Cancel"
                              >
                                <X size={18} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => openHistory(campaign)}
                                data-testid={`campaigns-history-${campaign.id}`}
                                className="p-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                                title="Edit history"
                              >
                                <History size={18} />
                              </button>
                              <button
                                onClick={() => startEdit(campaign)}
                                className="p-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                onClick={() => deleteCampaign(campaign.id)}
                                className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                                title="Delete"
                              >
                                <Trash2 size={18} />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Metrics Grid — 9 tiles: Sent, Delivered, Seen, Clicks, CTR, Delivery Rate, Net Spends, Revenue, ROAS */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-6">
                      {/* Sent */}
                      <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="text-blue-400" size={16} />
                          <span className="text-xs text-slate-400 font-medium">Sent</span>
                        </div>
                        {isEditing ? (
                          <input
                            type="number"
                            value={displayCampaign.sent || ''}
                            onChange={(e) => setEditingCampaign({...editingCampaign, sent: e.target.value})}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                          />
                        ) : (
                          <p className="text-xl font-bold text-white">{formatNumber(displayCampaign.sent)}</p>
                        )}
                      </div>

                      {/* Delivered */}
                      <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="text-green-400" size={16} />
                          <span className="text-xs text-slate-400 font-medium">Delivered</span>
                        </div>
                        {isEditing ? (
                          <input
                            type="number"
                            value={displayCampaign.delivered || ''}
                            onChange={(e) => setEditingCampaign({...editingCampaign, delivered: e.target.value})}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                          />
                        ) : (
                          <p className="text-xl font-bold text-white">{formatNumber(displayCampaign.delivered)}</p>
                        )}
                      </div>

                      {/* Seen */}
                      <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                        <div className="flex items-center gap-2 mb-2">
                          <Eye className="text-purple-400" size={16} />
                          <span className="text-xs text-slate-400 font-medium">Seen</span>
                        </div>
                        {isEditing ? (
                          <input
                            type="number"
                            value={displayCampaign.seen || ''}
                            onChange={(e) => setEditingCampaign({...editingCampaign, seen: e.target.value})}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                          />
                        ) : (
                          <p className="text-xl font-bold text-white">{formatNumber(displayCampaign.seen)}</p>
                        )}
                      </div>

                      {/* Clicks */}
                      <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                        <div className="flex items-center gap-2 mb-2">
                          <MousePointer className="text-yellow-400" size={16} />
                          <span className="text-xs text-slate-400 font-medium">Clicks</span>
                        </div>
                        {isEditing ? (
                          <input
                            type="number"
                            value={displayCampaign.clicks || ''}
                            onChange={(e) => setEditingCampaign({...editingCampaign, clicks: e.target.value})}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                          />
                        ) : (
                          <p className="text-xl font-bold text-white">{formatNumber(displayCampaign.clicks)}</p>
                        )}
                      </div>

                      {/* CTR */}
                      <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700" data-testid={`campaign-ctr-tile-${campaign.id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <MousePointer className="text-yellow-300" size={16} />
                          <span className="text-xs text-slate-400 font-medium">CTR</span>
                        </div>
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={displayCampaign.ctr ?? ''}
                            onChange={(e) => setEditingCampaign({...editingCampaign, ctr: e.target.value})}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                          />
                        ) : (
                          <p className="text-xl font-bold text-white">
                            {displayCampaign.ctr != null && displayCampaign.ctr !== '' ? `${Number(displayCampaign.ctr)}%` : '—'}
                          </p>
                        )}
                      </div>

                      {/* Delivery Rate */}
                      <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700" data-testid={`campaign-delivery-rate-tile-${campaign.id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="text-emerald-400" size={16} />
                          <span className="text-xs text-slate-400 font-medium">Delivery Rate</span>
                        </div>
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={displayCampaign.delivery_rate ?? ''}
                            onChange={(e) => setEditingCampaign({...editingCampaign, delivery_rate: e.target.value})}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                          />
                        ) : (
                          <p className="text-xl font-bold text-white">
                            {displayCampaign.delivery_rate != null && displayCampaign.delivery_rate !== '' ? `${Number(displayCampaign.delivery_rate)}%` : '—'}
                          </p>
                        )}
                      </div>

                      {/* Net Spends */}
                      <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="text-red-400" size={16} />
                          <span className="text-xs text-slate-400 font-medium">Net Spends</span>
                        </div>
                        {isEditing ? (
                          <input
                            type="number"
                            value={displayCampaign.net_spends || ''}
                            onChange={(e) => setEditingCampaign({...editingCampaign, net_spends: e.target.value})}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                          />
                        ) : (
                          <p className="text-xl font-bold text-white">{formatCurrency(displayCampaign.net_spends)}</p>
                        )}
                      </div>

                      {/* Revenue */}
                      <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="text-teal-400" size={16} />
                          <span className="text-xs text-slate-400 font-medium">Revenue</span>
                        </div>
                        {isEditing ? (
                          <input
                            type="number"
                            value={displayCampaign.sales || ''}
                            onChange={(e) => setEditingCampaign({...editingCampaign, sales: e.target.value})}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                          />
                        ) : (
                          <p className="text-xl font-bold text-white">{formatCurrency(displayCampaign.sales)}</p>
                        )}
                      </div>

                      {/* ROAS */}
                      <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                        <div className="flex items-center gap-2 mb-2">
                          <Target className="text-orange-400" size={16} />
                          <span className="text-xs text-slate-400 font-medium">ROAS</span>
                        </div>
                        {isEditing ? (
                          <input
                            type="number"
                            step="0.01"
                            value={displayCampaign.roas || ''}
                            onChange={(e) => setEditingCampaign({...editingCampaign, roas: e.target.value})}
                            className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                          />
                        ) : (
                          <p className="text-xl font-bold text-white">
                            {displayCampaign.roas != null && displayCampaign.roas !== '' ? `${Number(displayCampaign.roas)}x` : '—'}
                          </p>
                        )}
                      </div>

                      {/* Additional Fields in Editing Mode */}
                      {isEditing && (
                        <>
                          <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                            <span className="text-xs text-slate-400 font-medium block mb-2">Cohort</span>
                            <input
                              type="text"
                              value={displayCampaign.cohort || ''}
                              onChange={(e) => setEditingCampaign({...editingCampaign, cohort: e.target.value})}
                              className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                              placeholder="Cohort"
                            />
                          </div>

                          <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
                            <span className="text-xs text-slate-400 font-medium block mb-2">Attribution</span>
                            <input
                              type="text"
                              value={displayCampaign.attribution_setting || ''}
                              onChange={(e) => setEditingCampaign({...editingCampaign, attribution_setting: e.target.value})}
                              className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                              placeholder="Attribution"
                            />
                          </div>

                          <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700 col-span-2">
                            <span className="text-xs text-slate-400 font-medium block mb-2">Landing Page</span>
                            <input
                              type="text"
                              value={displayCampaign.landing_page || ''}
                              onChange={(e) => setEditingCampaign({...editingCampaign, landing_page: e.target.value})}
                              className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                              placeholder="Landing page URL"
                            />
                          </div>
                        </>
                      )}
                    </div>

                    {/* Meta chips (view mode) — Cohort, Attribution, Landing Page */}
                    {!isEditing && (displayCampaign.cohort || displayCampaign.attribution_setting || displayCampaign.landing_page) && (
                      <div className="flex flex-wrap gap-2 mt-3" data-testid={`campaign-meta-row-${campaign.id}`}>
                        {displayCampaign.cohort && (
                          <div
                            className="inline-flex items-center gap-2 bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-1.5"
                            data-testid={`campaign-cohort-chip-${campaign.id}`}
                          >
                            <Users className="text-cyan-400 flex-shrink-0" size={14} />
                            <span className="text-[11px] text-slate-400 font-medium">Cohort:</span>
                            <span className="text-xs text-white font-medium">{displayCampaign.cohort}</span>
                          </div>
                        )}
                        {displayCampaign.attribution_setting && (
                          <div
                            className="inline-flex items-center gap-2 bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-1.5"
                            data-testid={`campaign-attribution-chip-${campaign.id}`}
                          >
                            <Tag className="text-pink-400 flex-shrink-0" size={14} />
                            <span className="text-[11px] text-slate-400 font-medium">Attribution:</span>
                            <span className="text-xs text-white font-medium">{displayCampaign.attribution_setting}</span>
                          </div>
                        )}
                        {displayCampaign.landing_page && (
                          <a
                            href={displayCampaign.landing_page}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={displayCampaign.landing_page}
                            className="inline-flex items-center gap-2 bg-slate-900/50 hover:bg-slate-900/80 border border-slate-700 hover:border-teal-500/60 rounded-lg px-3 py-1.5 max-w-[420px] transition-colors group"
                            data-testid={`campaign-landing-page-link-${campaign.id}`}
                          >
                            <LinkIcon className="text-teal-400 flex-shrink-0" size={14} />
                            <span className="text-[11px] text-slate-400 font-medium">Landing:</span>
                            <span className="text-xs text-teal-300 group-hover:text-teal-200 font-medium truncate">
                              {displayCampaign.landing_page}
                            </span>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Floating Compare action bar (visible only when Compare mode is active) */}
      {compareMode && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-3 rounded-2xl bg-slate-900 border border-teal-500/60 shadow-2xl shadow-teal-500/20"
          data-testid="compare-action-bar"
        >
          <div className="flex items-center gap-2 text-sm text-white">
            <TrendingUp size={14} className="text-teal-400" />
            <span className="font-semibold">{selectedForCompare.length}</span>
            <span className="text-slate-400">of 5 selected</span>
          </div>
          <button
            onClick={() => setSelectedForCompare([])}
            disabled={selectedForCompare.length === 0}
            className="px-3 py-1.5 text-xs rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="compare-clear-selection"
          >
            Clear
          </button>
          <button
            onClick={launchCompare}
            disabled={selectedForCompare.length < 2}
            className="px-4 py-1.5 text-xs font-bold rounded-lg text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: TEAL }}
            data-testid="compare-launch"
          >
            Compare Now →
          </button>
        </div>
      )}

      {/* Add Record Modal */}
      {addModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setAddModalOpen(false); }}
          data-testid="campaigns-data-add-modal"
        >
          <div className="bg-slate-800 dark:bg-slate-800 light:bg-white border border-slate-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-700 sticky top-0 bg-slate-800 z-10">
              <div className="flex items-center gap-2">
                <Plus size={20} style={{ color: TEAL }} />
                <h2 className="text-lg font-bold text-white">Add Campaign Record</h2>
              </div>
              <button
                onClick={() => setAddModalOpen(false)}
                className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Required */}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Campaign Name *</label>
                  <input
                    type="text"
                    value={newRecord.name}
                    onChange={(e) => setNewRecord({ ...newRecord, name: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="e.g. Wellcore - Republic Day Drop"
                    data-testid="add-record-name"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Brand *</label>
                  <select
                    value={newRecord.brand_id}
                    onChange={(e) => setNewRecord({ ...newRecord, brand_id: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="add-record-brand"
                  >
                    <option value="">Select brand</option>
                    {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Date *</label>
                  <input
                    type="date"
                    value={newRecord.date}
                    onChange={(e) => setNewRecord({ ...newRecord, date: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="add-record-date"
                  />
                </div>
              </div>

              {/* Creative */}
              <div>
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Creative URL (optional)</label>
                <div className="flex items-center gap-2">
                  <LinkIcon size={14} className="text-slate-500" />
                  <input
                    type="url"
                    value={newRecord.creative}
                    onChange={(e) => setNewRecord({ ...newRecord, creative: e.target.value })}
                    placeholder="https://drive.google.com/... or any image URL"
                    className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    data-testid="add-record-creative-url"
                  />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">After creating, you can also upload an image file from the card.</p>
              </div>

              {/* Metrics */}
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Metrics</p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: 'sent', label: 'Sent' },
                    { key: 'delivered', label: 'Delivered' },
                    { key: 'seen', label: 'Seen' },
                    { key: 'clicks', label: 'Clicks' },
                    { key: 'ctr', label: 'CTR (%)', step: '0.01' },
                    { key: 'delivery_rate', label: 'Delivery Rate (%)', step: '0.01' },
                    { key: 'net_spends', label: 'Net Spends (₹)' },
                    { key: 'sales', label: 'Sales (₹)' },
                    { key: 'roas', label: 'ROAS (x)', step: '0.01' },
                  ].map(({ key, label, step }) => (
                    <div key={key}>
                      <label className="text-[10px] text-slate-500 block mb-1">{label}</label>
                      <input
                        type="number"
                        step={step || '1'}
                        value={newRecord[key] === 0 ? '' : newRecord[key]}
                        onChange={(e) => setNewRecord({ ...newRecord, [key]: e.target.value })}
                        placeholder="0"
                        className="w-full px-2 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        data-testid={`add-record-${key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Misc */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Cohort</label>
                  <input
                    type="text"
                    value={newRecord.cohort}
                    onChange={(e) => setNewRecord({ ...newRecord, cohort: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Attribution</label>
                  <input
                    type="text"
                    value={newRecord.attribution_setting}
                    onChange={(e) => setNewRecord({ ...newRecord, attribution_setting: e.target.value })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wide block mb-1.5">Landing Page</label>
                  <input
                    type="url"
                    value={newRecord.landing_page}
                    onChange={(e) => setNewRecord({ ...newRecord, landing_page: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-5 border-t border-slate-700 sticky bottom-0 bg-slate-800">
              <button
                onClick={() => setAddModalOpen(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleAddRecord}
                disabled={savingNew}
                data-testid="add-record-save"
                className="px-4 py-2 text-white rounded-xl hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: TEAL }}
              >
                {savingNew ? 'Saving…' : 'Add Record'}
              </button>
            </div>
          </div>
        </div>
      )}

      {libraryPickerFor && (
        <CreativeLibraryPickerModal
          onClose={() => setLibraryPickerFor(null)}
          onSelect={(libraryItem) => handleLinkFromLibrary(libraryPickerFor, libraryItem.url)}
          showToast={showToast}
        />
      )}

      {/* Edit-history modal (per-campaign audit trail) */}
      {historyCampaign && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="campaigns-history-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setHistoryCampaign(null); }}
        >
          <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <History size={16} className="text-slate-400" /> Edit history
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {historyCampaign.campaign_name || historyCampaign.name || historyCampaign.brand || 'Campaign'}
                </p>
              </div>
              <button
                onClick={() => setHistoryCampaign(null)}
                className="p-1.5 rounded hover:bg-slate-700 text-slate-400"
                data-testid="campaigns-history-close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {historyLoading ? (
                <div className="text-slate-400 text-sm text-center py-8">Loading history…</div>
              ) : historyEntries.length === 0 ? (
                <div className="text-slate-400 text-sm text-center py-8" data-testid="campaigns-history-empty">
                  No edits recorded yet for this campaign.
                </div>
              ) : (
                <ul className="space-y-3" data-testid="campaigns-history-list">
                  {historyEntries.map((entry) => {
                    const ts = entry.timestamp ? new Date(entry.timestamp) : null;
                    return (
                      <li key={entry.id} className="border border-slate-700 rounded-lg p-3 bg-slate-900/50">
                        <div className="flex items-center justify-between mb-2 gap-2">
                          <div className="text-xs text-slate-200 flex items-center flex-wrap gap-1.5">
                            <span className="font-semibold text-white">
                              {entry.user_name || entry.user_email || 'Unknown user'}
                            </span>
                            {entry.user_role && (
                              <span className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 text-[10px] uppercase tracking-wider">
                                {entry.user_role}
                              </span>
                            )}
                            {entry.revert && (
                              <span className="px-1.5 py-0.5 rounded bg-amber-900/60 text-amber-200 text-[10px] uppercase tracking-wider font-semibold">
                                Revert
                              </span>
                            )}
                            {entry.reverted && (
                              <span className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 text-[10px] uppercase tracking-wider font-semibold">
                                Reverted
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="text-[11px] text-slate-400 tabular-nums">
                              {ts ? ts.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                            </div>
                            {isAdmin && !entry.revert && !entry.reverted && (
                              <button
                                onClick={() => handleHistoryRevert(entry)}
                                disabled={historyRevertingId === entry.id}
                                data-testid={`campaigns-history-revert-${entry.id}`}
                                className="flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded border border-amber-600 text-amber-300 bg-transparent hover:bg-amber-900/30 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                title="Revert this edit"
                              >
                                <Undo2 size={11} />
                                {historyRevertingId === entry.id ? '…' : 'Revert'}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="space-y-1">
                          {(entry.changes || []).map((c, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs">
                              <span className="font-medium text-slate-400 min-w-[100px] capitalize truncate">
                                {String(c.field).replace(/_/g, ' ')}
                              </span>
                              <span className="text-red-400 line-through tabular-nums truncate max-w-[140px]">
                                {c.before === null || c.before === undefined ? '—' : String(c.before)}
                              </span>
                              <span className="text-slate-500">→</span>
                              <span className="text-teal-300 font-semibold tabular-nums truncate max-w-[140px]">
                                {c.after === null || c.after === undefined ? '—' : String(c.after)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Creative Library picker modal ----------
const CreativeLibraryPickerModal = ({ onClose, onSelect, showToast }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [previewing, setPreviewing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async (q) => {
    setLoading(true);
    try {
      const r = await listCreatives({ search: q || undefined });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      showToast?.(e.response?.data?.detail || 'Failed to load library', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load();   }, []);

  const filtered = items.filter((c) => {
    if (c.status === 'archived') return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) || (c.tags || []).some((t) => t.toLowerCase().includes(q));
  });

  const onUploadNew = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(ext)) {
      showToast?.('Unsupported file type. Allowed: JPG, JPEG, PNG, WEBP, GIF, SVG', 'error');
      return;
    }
    setUploading(true);
    try {
      const res = await uploadCreative(f, { name: f.name.replace(/\.[^.]+$/, '') });
      const newItem = res.data;
      showToast?.('Uploaded to library — linking...', 'success');
      onSelect(newItem); // auto-link the new upload
    } catch (err) {
      showToast?.(err.response?.data?.detail || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="campaigns-library-picker-modal">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Link from Creative Library</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Pick an existing creative — or upload a new one and link instantly.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>

        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search creatives by name or tag..."
              data-testid="library-picker-search"
              className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <label className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white rounded-lg cursor-pointer hover:opacity-90"
            style={{ backgroundColor: TEAL }}>
            <Upload size={13} />
            {uploading ? 'Uploading...' : 'Upload new'}
            <input type="file" accept=".jpg,.jpeg,.png,.webp,.gif,.svg" className="hidden" onChange={onUploadNew} data-testid="library-picker-upload" disabled={uploading} />
          </label>
        </div>

        <div className="overflow-y-auto p-5 flex-1" data-testid="library-picker-grid">
          {loading ? (
            <p className="text-xs text-slate-400 text-center py-10">Loading creative library...</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10">
              <ImageIcon size={28} className="mx-auto mb-2 text-slate-300" />
              <p className="text-sm font-semibold text-slate-700">{items.length === 0 ? 'Library is empty' : 'No matches'}</p>
              <p className="text-[11px] text-slate-500 mt-1">{items.length === 0 ? 'Upload a creative to populate the library.' : 'Try a different search term.'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filtered.map((c) => (
                <div key={c.id} className="bg-white border border-slate-200 hover:border-teal-400 rounded-xl overflow-hidden group flex flex-col transition-colors"
                  data-testid={`library-picker-card-${c.name}`}>
                  <button
                    onClick={() => setPreviewing(c)}
                    className="aspect-square bg-slate-50 hover:bg-slate-100 flex items-center justify-center relative">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${c.url}`} alt={c.name}
                      className="w-full h-full object-contain p-2" loading="lazy" />
                    <span className="absolute top-1.5 right-1.5 text-[9px] font-bold uppercase bg-white/90 text-slate-700 px-1.5 py-0.5 rounded">{c.ext}</span>
                  </button>
                  <div className="p-2 flex-1 flex flex-col">
                    <div className="text-[11px] font-bold text-slate-900 truncate" title={c.name}>{c.name}</div>
                    <div className="text-[9px] text-slate-400 mt-0.5">{new Date(c.created_at).toLocaleDateString()}</div>
                    {(c.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-0.5 mt-1">
                        {c.tags.slice(0, 2).map((t) => (
                          <span key={t} className="text-[9px] px-1 py-0.5 bg-teal-50 text-teal-700 rounded-full">#{t}</span>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => onSelect(c)}
                      data-testid={`library-picker-select-${c.name}`}
                      className="mt-2 w-full text-[11px] font-semibold py-1 rounded text-white hover:opacity-90"
                      style={{ backgroundColor: TEAL }}>
                      Link
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {previewing && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setPreviewing(null)}
          data-testid="library-picker-preview">
          <button className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white" onClick={() => setPreviewing(null)}>
            <X size={20} />
          </button>
          <div className="flex flex-col items-center gap-3 max-w-[90vw] max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <img src={`${process.env.REACT_APP_BACKEND_URL}${previewing.url}`} alt={previewing.name}
              className="max-w-full max-h-[78vh] object-contain rounded-lg shadow-2xl" />
            <div className="text-white text-center">
              <div className="text-sm font-semibold">{previewing.name}</div>
              <div className="text-[11px] opacity-70 mt-0.5">
                {previewing.ext?.toUpperCase()} · {((previewing.size_bytes || 0) / 1024).toFixed(1)} KB · uploaded {new Date(previewing.created_at).toLocaleString()}
              </div>
            </div>
            <button onClick={() => { onSelect(previewing); setPreviewing(null); }}
              className="px-5 py-2 text-sm font-semibold text-white rounded-lg hover:opacity-90"
              style={{ backgroundColor: TEAL }}>
              Link this creative
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllCampaignsData;
